import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';
import { traceCall } from './callTrace.js';

// Bridges the native incoming-call surface (iOS: CallKit in the app's own
// IncomingCallBridge.swift; Android: the full-screen call notification in
// CallMessagingService.java) to the SIP layer that already lives in this
// component.
//
// Division of labour:
//
//   native side  - owns waking the device, drawing the call screen, the
//                  ringtone, and the answer/decline buttons. It knows nothing
//                  about SIP. On iOS it only rings: WKWebView cannot record or
//                  play while a CallKit call holds the audio session, so an
//                  answered call is ended in CallKit, and callAnswered arrives
//                  only after CallKit has released the session.
//   this mixin   - registers device tokens, makes sure SIP is registered when
//                  a wake-up lands, and turns the native events into the
//                  in-app call: the call dialog plus acceptIncomingCall() on
//                  an answer, rejectIncomingCall() on a decline.
//
// Everything here is inert outside the mobile shells: without
// window.Capacitor the plugin lookup returns null and every method returns
// early, so the browser build is untouched.

// How long to wait for the INVITE after the user has answered on the native
// screen. The device still has to register with FreeSWITCH before the call
// can be routed to it, and on a cold start that is seconds - but not
// unbounded, and a stuck "connecting" screen is worse than a clear failure.
const INVITE_WAIT_MS = 15_000;

// How long a woken app gives its existing registration to prove it is still
// alive. After a suspend the agent can read "registered" over a socket that
// died with the suspend, and an INVITE sent there never arrives.
const WAKE_PROBE_TIMEOUT = 3000;

export const nativeCallsMixin = {

    //********************* NATIVE CALLS: SETUP ********************************//
    _plugins() {
        return (window.Capacitor && window.Capacitor.Plugins) || {};
    },

    // GhVoip is this app's own local plugin (ios/App/App/GhVoipPlugin.swift,
    // android/.../GhVoipPlugin.java). A third-party plugin cannot own this
    // path: the events originate in a VoIP push handled before any WebView
    // exists.
    _voip() {
        return this._plugins().GhVoip || null;
    },

    isNativeShell() {
        return Boolean(window.Capacitor?.isNativePlatform?.());
    },

    // Called once the component knows which user it is acting for - the token
    // is worthless to the server without a user id to file it under.
    async initNativeCalls() {
        if(!this.isNativeShell()) return;

        const voip = this._voip();
        if(!voip) {
            console.warn('[native-calls] GhVoip plugin not available');
            return;
        }

        // Waited for: it decides how this tab connects (sipRegistrationMixin -
        // a native engine takes the incoming calls, so the tab only connects
        // for outgoing ones), and answering it is instant.
        this.nativeEngine = await this._hasNativeEngine();

        if(this.nativeEngine) {
            // Not waited for. The engine's setup and the push token's upload
            // go through the OS and the network - Android's asks the user for
            // the microphone and notifications on the way - and the caller of
            // this connects for outgoing calls next, which must never wait on
            // either: until it connects every call is "Still connecting".
            this.startNativeEngine()
                .then(() => this._registerDeviceToken())
                .catch((err) => console.warn('[native-calls]', err?.message || err));
            return;
        }

        // The WebView engine (Android builds before the native one). Listeners
        // first: on a cold start from a push the retained incomingCall is what
        // starts SIP registration, and it should not sit behind the token
        // upload's network round trip.
        this._listenForNativeCallEvents();
        await this._registerDeviceToken();
    },

    // onInit() can run more than once for the same element, and every
    // addListener call makes Capacitor replay the events it retained for that
    // name (the plugin emits with retainUntilConsumed so a push that
    // cold-started the app is not lost). Subscribing twice therefore does not
    // just double the handlers - it re-delivers an already-handled incomingCall
    // for a call that has since been declined, which is what made the phone
    // ring a second time and left the web layer acting on a dead call.
    _listenersAttached: false,

    //********************* NATIVE CALLS: DEVICE TOKEN ********************************//
    // The token is produced by the native layer (PushKit on iOS, FCM on
    // Android) long before the WebView exists on a cold start from a push,
    // so the shell caches it and this reads it back out rather than waiting
    // for a JS-side event that may already have fired.
    // This device's push token and, from a native call engine that tells it,
    // the address the engine registers as.
    async _device() {
        // Asked of the native side first: it is the authority, and on a cold
        // start from a push the token was cached before any JS existed.
        const fromPlugin = await this._safe(() => this._voip()?.getToken());
        if(fromPlugin?.token) return { token: fromPlugin.token, aor: fromPlugin.aor || null };

        const Preferences = this._plugins().Preferences;
        if(!Preferences) return null;

        const stored = await this._safe(() => Preferences.get({ key: 'gh_device_token' }));
        return stored?.value ? { token: stored.value, aor: null } : null;
    },

    // The SIP server verifies this with the same shared-secret scheme it uses
    // for SIP REGISTER (server/Authentication/requireAuth.js), so the token
    // is the very same gudhub.getToken() value sipRegistrationMixin already
    // sends as authorizationUsername - not the localStorage auth_key, which
    // is a different credential and would fail verification.
    async _authHeaders() {
        const token = await gudhub.getToken();
        return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
    },

    async _apnsEnvironment() {
        const result = await this._safe(() => this._voip()?.getEnvironment());
        return result?.environment || null;
    },

    async _registerDeviceToken() {
        const device = await this._device();
        if(!device) return;

        const platform = window.Capacitor.getPlatform();   // 'ios' | 'android'

        await this._safe(async () => fetch(`${GUDHUB_SETTINGS.sip_server_url}/push/device/register`, {
            method: 'POST',
            headers: await this._authHeaders(),
            body: JSON.stringify({
                platform,
                // Asked of the native side rather than guessed here: it reads
                // the value out of the app's signed entitlements, which is the
                // same thing APNs keys off. Guessing it wrong produces a
                // server-side BadEnvironmentKeyInToken that never reaches the
                // device, so push simply stops working with no visible cause.
                environment: await this._apnsEnvironment(),
                // Who answers calls on this device. A native engine is waited
                // for by the dialplan before the user's other devices ring.
                engine: this.nativeEngine ? 'native' : 'webview',
                token: device.token,
                // The address this phone's call engine registers as - what lets
                // the SIP server tell it apart from the user's other phones when
                // a call ends on one of them (PushSender.sendCancel).
                aor: device.aor
            })
        }));
    },

    // Meant for logout, so the phone stops ringing for an account that is no
    // longer signed in on it. Must run BEFORE the session is cleared: the
    // endpoint is authenticated, and scoped to the caller's own rows.
    async unregisterDeviceToken() {
        const device = await this._device();
        if(!device) return;

        await this._safe(async () => fetch(`${GUDHUB_SETTINGS.sip_server_url}/push/device/unregister`, {
            method: 'DELETE',
            headers: await this._authHeaders(),
            body: JSON.stringify({ token: device.token })
        }));
    },

    //********************* NATIVE CALLS: EVENTS ********************************//
    _listenForNativeCallEvents() {
        if(this._listenersAttached) return;
        this._listenersAttached = true;

        const voip = this._voip();

        // Raised the moment a wake-up push lands, before the user has done
        // anything. Registering SIP now - rather than waiting for them to
        // answer - is what gives FreeSWITCH somewhere to route the call to
        // while it is still ringing on the server side.
        //
        // The native layer buffers events raised before this listener exists,
        // which is the normal case when the push cold-started the app, so no
        // call is missed by subscribing "late".
        voip.addListener('incomingCall', ({ callId, callerId, callerName }) => {
            traceCall('incomingCall', { callId, callerId, callerName, callState: this.callState });

            this.nativeCallActive = true;
            this.nativeCallId = callId || null;

            this._safe(() => this.ensureRegisteredForNativeCall());
            this._safe(() => this.refreshNativeCallerName(callId, callerId, callerName));
        });

        // On iOS this arrives only once the CallKit call has been ended and
        // CallKit has released the audio session (see IncomingCallBridge's
        // hand-off), so getUserMedia and playback in this WebView get the
        // audio to themselves. On Android it arrives when the notification's
        // Answer brought the app forward.
        voip.addListener('callAnswered', async ({ callId }) => {
            // Events are delivered with retainUntilConsumed, so one raised while
            // no WebView existed is replayed the next time this listener
            // attaches. Without this guard an answer tapped on a call that is
            // long over gets replayed on the NEXT launch and silently accepts
            // whatever INVITE happens to be pending - which is exactly how a
            // call showed as answered while the phone sat untouched.
            traceCall('callAnswered', {
                callId,
                current: this.nativeCallId,
                active: this.nativeCallActive,
                callState: this.callState,
                hasSession: Boolean(this.session),
                hasPending: Boolean(this.pendingNativeInvitation)
            });

            if(callId && this.nativeCallId && callId !== this.nativeCallId) return;
            if(!this.nativeCallActive) return;

            // A call that has already been declined must never be accepted,
            // whatever order its events arrive in. Retained events are
            // replayed when a listener attaches, so an answer raised before
            // the decline can surface after it - and accepting then sends a
            // 200 OK for a call the user visibly rejected, which is what made
            // the caller's screen show a connected call.
            if(this._endedCallIds?.has(callId)) return;

            this.nativeCallId = callId || this.nativeCallId;

            const invitation = await this._waitForInvitation();
            const otherUser = invitation ? await this.pendingNativeCaller : null;

            // The INVITE never arrived (the caller hung up, or the wake path
            // timed out server-side), or the call ended while this waited.
            // Close the native screen rather than leaving it spinning.
            if(!invitation || this.session !== invitation) {
                traceCall('inviteMissing', { callId, callState: this.callState });
                this._endNativeCall();
                return;
            }

            // Nothing native is on screen any more - iOS ended its CallKit call
            // for the hand-off, Android's notification opened this app - so the
            // in-app dialog is the call UI from here on. It also holds the
            // <video> element attachMediaStreams() plays the remote audio
            // through: accepted without it, the call connects and nobody is
            // heard. Opened before the accept so that element exists by the
            // time the session is Established.
            this.showCallDialog({ otherUser, callState: 'connecting' });

            traceCall('accepting', { callState: this.callState, sessionState: this.session?.state });
            await this.acceptIncomingCall();
            traceCall('accepted', { callState: this.callState, sessionState: this.session?.state });
        });

        voip.addListener('callEnded', async ({ callId, reason }) => {
            traceCall('callEnded', {
                callId,
                reason,
                current: this.nativeCallId,
                callState: this.callState,
                hasSession: Boolean(this.session)
            });

            // Recorded before the id guard below, so a decline is remembered
            // even for a call this instance was not tracking.
            (this._endedCallIds ??= new Set()).add(callId);

            if(callId && this.nativeCallId && callId !== this.nativeCallId) return;

            // Declining and hanging up are the same CallKit action, so one
            // event covers both, and both calls below are safe when the other
            // one was the right answer.
            //
            // The wait matters: the user can decline before the INVITE has even
            // arrived, and rejecting nothing leaves the caller ringing into a
            // phone that already said no. Waiting for the invitation first lets
            // the 486 actually go out, which the dialplan's
            // fail_on_single_reject=USER_BUSY then turns into an immediate
            // busy for the caller instead of a full timeout.
            if(!this.session && !this.pendingNativeInvitation) {
                await this._waitForInvitation();
            }

            this._safe(() => this.rejectIncomingCall());
            this._safe(() => this.hangupCall());
            this._clearNativeCall();
        });
    },

    // The push carries the caller's GudHub user id, not their name - the SIP
    // server has no authenticated way to read one. So the native screen comes
    // up immediately showing the id, and this replaces it with the real name a
    // moment later, once the WebView has booted far enough to make the lookup.
    // On the lock screen that reads as the name simply appearing, the way it
    // does in any messenger.
    //
    // Deliberately fire-and-forget: a failed lookup leaves the id on screen,
    // which is worse-looking but still a usable call.
    async refreshNativeCallerName(callId, callerId, callerName) {
        // callerName is the fallback for a server that has not yet been
        // updated to send caller_id: it arrives as the caller's user id
        // already stripped of its session suffix, so a purely numeric value is
        // exactly the id needed here. A PSTN caller's number is numeric too,
        // but getUserById simply finds nobody and the screen keeps the number.
        const userId = callerId || (/^\d+$/.test(callerName || '') ? callerName : '');

        if(!callId || !userId) return;

        const voip = this._voip();
        if(!voip) return;

        const user = await this._safe(() => gudhub.getUserById(String(userId)));
        const name = user?.fullname;

        // Nothing resolved, or the call ended while the lookup was in flight.
        if(!name || this.nativeCallId !== callId) return;

        await this._safe(() => voip.updateCall({ callId, callerName: name }));
    },

    //********************* NATIVE CALLS: SIP GLUE ********************************//
    // A wake-up must leave this device registered, without ever disturbing a
    // call: a ringing or live call proves the agent works, and tearing the
    // agent down would kill that call.
    //
    //   - a registration or reconnect already in flight is exactly what is
    //     needed, so it is joined rather than raced (onInit's retry loop is
    //     usually already running on a cold start);
    //   - no agent at all is a cold start: register;
    //   - an agent that reads "registered" may be talking to a socket that
    //     died with the suspend, so it is probed rather than trusted, and
    //     replaced through the same reconnect path the heartbeat uses.
    async ensureRegisteredForNativeCall() {
        if(this.callState !== 'registered' && this.callState !== 'idle') return;

        const inFlight = this._registration || this._reconnectPromise;
        if(inFlight) return inFlight;

        if(!this.userAgent) return this.registerSip();

        if(this.callState === 'registered') {
            try {
                await this.sendOptionsPing(WAKE_PROBE_TIMEOUT);
                return;
            } catch (err) {
                // An INVITE that arrived while the probe was out proves the
                // transport - and a reconnect now would tear that call down.
                if(this.session) return;
                traceCall('wakeProbeFailed', { error: err?.message });
            }
        }

        return this.handleTransportDisconnect(new Error('Registration did not survive the suspend'));
    },

    // handleIncomingInvite stores the invitation instead of opening its own
    // dialog while a native call is active (see callSessionMixin). It may
    // already be here by the time the user answers, or still be in flight.
    _waitForInvitation() {
        if(this.pendingNativeInvitation) return Promise.resolve(this.pendingNativeInvitation);

        return new Promise((resolve) => {
            const started = Date.now();

            const poll = setInterval(() => {
                if(this.pendingNativeInvitation) {
                    clearInterval(poll);
                    resolve(this.pendingNativeInvitation);
                    return;
                }

                if(Date.now() - started > INVITE_WAIT_MS) {
                    clearInterval(poll);
                    resolve(null);
                }
            }, 200);
        });
    },

    // Closes the native call screen from this side - used when SIP ends the
    // call (remote hangup, cancel, failure) while the native UI is still up.
    // After the iOS hand-off the call is no longer in CallKit and the native
    // side treats this as a no-op.
    _endNativeCall() {
        const voip = this._voip();
        if(!voip || !this.nativeCallId) return;

        this._safe(() => voip.endCall({ callId: this.nativeCallId }));
        this._clearNativeCall();
    },

    // Called from callSessionMixin.teardownCall(), which is the one place every
    // call - answered, rejected, remotely cancelled or failed - passes through.
    // Also closes the native screen, so a SIP-side hangup cannot leave CallKit
    // showing a call that no longer exists.
    clearNativeCallState() {
        if(!this.nativeCallActive) return;

        const voip = this._voip();
        if(voip && this.nativeCallId) {
            this._safe(() => voip.endCall({ callId: this.nativeCallId }));
        }

        this._clearNativeCall();
    },

    _clearNativeCall() {
        this.nativeCallActive = false;
        this.nativeCallId = null;
        this.pendingNativeInvitation = null;
        this.pendingNativeCaller = null;
    },

    async _safe(fn) {
        try {
            return await fn();
        } catch (err) {
            console.warn('[native-calls]', err?.message || err);
            return null;
        }
    }
};
